/**
 * ElecBid — CEC 2024 Voltage Drop Calculator
 * Browning Electric
 *
 * UI for the engine in src/utils/cecVoltageDrop.mjs — CEC 2024 (CSA C22.1:24)
 * Rule 8-102 + Appendix D Table D3 (K values), with:
 *   - full Note 2 system/circuit selection (f = 2 or 1.73)
 *   - cable vs raceway columns, DC / 100 / 90 / 80% pf
 *   - Note 3 operating-temperature multipliers, Note 4 extra-low-voltage
 *   - parallel runs, metres/feet, 3% / 5% / custom limits (Rule 8-102 1))
 *   - load entry as amps, breaker ×80% (Rule 8-102 1)), or watts (Rule 8-100)
 *   - ampacity cross-check (Tables 2 & 4), Rule 12-108 parallel floor
 *   - Table 68 dwelling-circuit alternate path callout (Rule 8-102 3))
 *   - max run length at the limit + minimum conductor size recommendation
 */

import { useMemo, useState } from "react";
import {
  SIZES, F_TABLE, getFRow, ELV_MAX_VOLTAGE,
  calcVoltageDrop, minSizeForLimit, table68Length,
  currentFromBreaker, currentFromWatts,
  feetToMetres, metresToFeet, labelForSize,
} from "../utils/cecVoltageDrop.mjs";

const ACCENT = "#8b5cf6";

// Voltage presets by circuit basis (Rule 8-100 divisors + common DC/ELV).
function voltagePresets(fRow) {
  if (!fRow) return [120, 240];
  if (fRow.phase === "dc") return [12, 24, 48, 125];
  if (fRow.basis.startsWith("line-to-grounded")) return [120, 277, 347];
  return [208, 240, 416, 480, 600];
}

const BREAKER_PRESETS = [15, 20, 30, 40, 60, 100, 125, 200, 400];

const CSS = `
  .vd-wrap { font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #e8eaf0; }
  .vd-section { margin-bottom: 16px; }
  .vd-section-title {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.07em; color: ${ACCENT}; margin-bottom: 8px;
  }
  .vd-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .vd-label { font-size: 12px; color: #8892a4; }
  .vd-hint { font-size: 10.5px; color: #6b7589; margin-top: 5px; line-height: 1.45; }

  .vd-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .vd-pill {
    padding: 6px 12px; border-radius: 6px; border: 1.5px solid #2a3140;
    background: #1e2430; color: #8892a4; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; font-family: inherit;
  }
  .vd-pill.active {
    border-color: ${ACCENT}; background: rgba(139,92,246,0.14); color: #c4b5fd;
  }
  .vd-pill:disabled { opacity: 0.35; cursor: not-allowed; }
  .vd-pill.sm { padding: 5px 9px; font-size: 11px; }

  .vd-select {
    width: 100%; background: #1e2430; border: 1px solid #2a3140; border-radius: 8px;
    color: #e8eaf0; font-size: 13px; font-weight: 600; padding: 9px 10px;
    font-family: inherit; outline: none;
  }
  .vd-select:focus { border-color: ${ACCENT}; }

  .vd-input {
    width: 90px; background: #1e2430; border: 1px solid #2a3140; border-radius: 6px;
    color: #e8eaf0; font-size: 13px; font-weight: 600; padding: 7px 9px;
    font-family: 'IBM Plex Mono', monospace; outline: none; text-align: right;
  }
  .vd-input:focus { border-color: ${ACCENT}; }
  .vd-unit { font-size: 11px; color: #555f72; }

  .vd-derived {
    font-size: 12px; font-family: 'IBM Plex Mono', monospace; color: #c4b5fd;
    background: rgba(139,92,246,0.08); padding: 4px 10px; border-radius: 5px;
  }

  .vd-check {
    display: flex; align-items: center; gap: 8px; cursor: pointer;
    font-size: 12px; color: #8892a4; user-select: none;
  }
  .vd-check input { accent-color: ${ACCENT}; width: 15px; height: 15px; cursor: pointer; }

  /* Result panel */
  .vd-result {
    border-radius: 12px; padding: 16px; margin-top: 4px;
    border: 1.5px solid #2a3140; background: #12151b;
  }
  .vd-result.pass { border-color: rgba(34,197,94,0.45); }
  .vd-result.fail { border-color: rgba(239,68,68,0.5); }
  .vd-result-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
  .vd-result-pct { font-size: 32px; font-weight: 800; font-family: 'IBM Plex Mono', monospace; }
  .vd-result-pct.pass { color: #22c55e; }
  .vd-result-pct.fail { color: #ef4444; }
  .vd-result-verdict { font-size: 12px; font-weight: 700; letter-spacing: 0.05em; }
  .vd-result-verdict.pass { color: #22c55e; }
  .vd-result-verdict.fail { color: #ef4444; }
  .vd-stats {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 12px;
  }
  .vd-stat { background: #1a1e26; border: 1px solid #262c38; border-radius: 8px; padding: 8px 10px; }
  .vd-stat-label { font-size: 9.5px; color: #6b7589; text-transform: uppercase; letter-spacing: 0.05em; }
  .vd-stat-value { font-size: 14px; font-weight: 700; font-family: 'IBM Plex Mono', monospace; margin-top: 3px; color: #e8eaf0; }
  .vd-trace {
    margin-top: 12px; padding: 9px 11px; background: #1a1e26; border-radius: 8px;
    font-size: 11px; font-family: 'IBM Plex Mono', monospace; color: #8892a4;
    line-height: 1.6; overflow-x: auto; white-space: nowrap;
  }
  .vd-detail { font-size: 11px; color: #6b7589; margin-top: 8px; line-height: 1.55; }
  .vd-detail b { color: #a7b0c0; font-weight: 600; }

  .vd-reco {
    margin-top: 10px; padding: 10px 12px; border-radius: 8px;
    background: rgba(139,92,246,0.07); border: 1px solid rgba(139,92,246,0.2);
    font-size: 12px; color: #c4c9d4; line-height: 1.55;
  }
  .vd-reco b { color: #c4b5fd; }

  .vd-warn {
    margin-top: 8px; padding: 9px 11px; border-radius: 8px;
    background: rgba(245,158,11,0.07); border: 1px solid rgba(245,158,11,0.25);
    font-size: 11.5px; color: #fbbf24; line-height: 1.5;
  }
  .vd-error {
    padding: 10px 12px; border-radius: 8px; background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.3); font-size: 12px; color: #fca5a5;
  }
  .vd-t68 {
    margin-top: 10px; padding: 10px 12px; border-radius: 8px;
    background: rgba(59,130,246,0.07); border: 1px solid rgba(59,130,246,0.25);
    font-size: 11.5px; color: #93c5fd; line-height: 1.55;
  }
  .vd-footnote { font-size: 10px; color: #555f72; margin-top: 14px; line-height: 1.5; }
`;

export default function VoltageDropCalc() {
  const [state, setState] = useState({
    fId: "sp_3w_ll",
    voltage: 240,
    voltageCustom: "",
    loadMode: "breaker",       // amps | breaker | watts
    amps: "",
    breaker: 40,
    watts: "",
    material: "cu",
    size: "6",
    install: "cable",
    pf: "90",                   // dc | 100 | 90 | 80
    temp: 75,
    parallel: 1,
    elv: false,
    length: 30,
    lengthUnit: "m",           // m | ft
    limitMode: "3",            // 3 | 5 | custom
    limitCustom: "",
  });

  const set = patch => setState(s => ({ ...s, ...patch }));

  const fRow = getFRow(state.fId);
  const isDc = fRow?.phase === "dc";

  // Derived inputs the engine consumes
  const voltage = state.voltageCustom !== "" ? Number(state.voltageCustom) : state.voltage;
  const current = useMemo(() => {
    if (state.loadMode === "amps") return Number(state.amps) || 0;
    if (state.loadMode === "breaker") return currentFromBreaker(Number(state.breaker) || 0);
    return currentFromWatts(Number(state.watts) || 0, voltage, state.fId);
  }, [state.loadMode, state.amps, state.breaker, state.watts, voltage, state.fId]);

  const lengthM = state.lengthUnit === "ft"
    ? feetToMetres(Number(state.length) || 0)
    : Number(state.length) || 0;

  const limitPct = state.limitMode === "custom"
    ? Number(state.limitCustom) || 0
    : Number(state.limitMode);

  const engineParams = {
    material: state.material, size: state.size, install: state.install,
    pf: isDc ? "dc" : state.pf, fId: state.fId,
    voltage, current, lengthM,
    temp: state.temp, elv: state.elv, parallel: state.parallel, limitPct,
  };

  const result = useMemo(() => calcVoltageDrop(engineParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, voltage, current, lengthM, limitPct]);

  const minSizes = useMemo(() => {
    if (!result.ok) return null;
    return minSizeForLimit(engineParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, voltage, current, lengthM, limitPct, result.ok]);

  // Table 68 applicability: 120 V 1φ 2-wire L-G, copper 14/12/10, 15/20 A breaker
  const t68 = (!isDc && state.fId === "sp_2w_lg" && voltage === 120
    && state.material === "cu" && state.loadMode === "breaker"
    && [15, 20].includes(Number(state.breaker)))
    ? table68Length(state.size, Number(state.breaker)) : null;

  const presets = voltagePresets(fRow);
  const showElv = voltage > 0 && voltage <= ELV_MAX_VOLTAGE;

  function selectSystem(fId) {
    const row = getFRow(fId);
    const patch = { fId };
    if (row.phase === "dc") {
      patch.pf = "dc";
      if (![12, 24, 48, 125].includes(state.voltage) && state.voltageCustom === "") patch.voltage = 24;
    } else {
      if (state.pf === "dc") patch.pf = "90";
      const p = voltagePresets(row);
      if (!p.includes(state.voltage) && state.voltageCustom === "") patch.voltage = p[0] === 120 ? 120 : 240;
      if (row.basis.startsWith("line-to-grounded") && state.voltage === 240 && state.voltageCustom === "") patch.voltage = 120;
    }
    set(patch);
  }

  const fmtLen = m => state.lengthUnit === "ft"
    ? `${metresToFeet(m).toFixed(1)} ft`
    : `${m.toFixed(1)} m`;

  return (
    <div className="vd-wrap">
      <style>{CSS}</style>

      {/* ── System & circuit ── */}
      <div className="vd-section">
        <div className="vd-section-title">System &amp; Circuit</div>
        <select
          className="vd-select"
          value={state.fId}
          onChange={e => selectSystem(e.target.value)}
        >
          {["DC", "1-phase AC", "3-phase AC"].map(sys => (
            <optgroup key={sys} label={sys}>
              {F_TABLE.filter(r => r.system === sys).map(r => (
                <option key={r.id} value={r.id}>
                  {r.circuit} (f = {r.f})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <div className="vd-hint">
          Table D3 Note 2 — voltage drop factor f = {fRow?.f}. Drop is measured {fRow?.basis}.
        </div>
        <div className="vd-row" style={{ marginTop: 8 }}>
          <span className="vd-label">Voltage</span>
          <div className="vd-pills">
            {presets.map(v => (
              <button
                key={v}
                className={`vd-pill sm${state.voltageCustom === "" && state.voltage === v ? " active" : ""}`}
                onClick={() => set({ voltage: v, voltageCustom: "" })}
              >{v} V</button>
            ))}
          </div>
          <input
            className="vd-input" style={{ width: 70 }} type="number" inputMode="decimal"
            placeholder="other" value={state.voltageCustom}
            onChange={e => set({ voltageCustom: e.target.value })}
          />
          <span className="vd-unit">V</span>
        </div>
      </div>

      {/* ── Load ── */}
      <div className="vd-section">
        <div className="vd-section-title">Load</div>
        <div className="vd-pills" style={{ marginBottom: 8 }}>
          <button className={`vd-pill${state.loadMode === "breaker" ? " active" : ""}`} onClick={() => set({ loadMode: "breaker" })}>Breaker × 80%</button>
          <button className={`vd-pill${state.loadMode === "amps" ? " active" : ""}`} onClick={() => set({ loadMode: "amps" })}>Amps</button>
          <button className={`vd-pill${state.loadMode === "watts" ? " active" : ""}`} onClick={() => set({ loadMode: "watts" })}>Watts</button>
        </div>

        {state.loadMode === "breaker" && (
          <>
            <div className="vd-pills">
              {BREAKER_PRESETS.map(b => (
                <button key={b}
                  className={`vd-pill sm${Number(state.breaker) === b ? " active" : ""}`}
                  onClick={() => set({ breaker: b })}
                >{b} A</button>
              ))}
              <input
                className="vd-input" style={{ width: 64 }} type="number" inputMode="numeric"
                value={state.breaker}
                onChange={e => set({ breaker: e.target.value })}
              />
            </div>
            <div className="vd-row" style={{ marginTop: 8 }}>
              <span className="vd-derived">{(Number(state.breaker) || 0)} A × 80% = {current.toFixed(1)} A design current</span>
            </div>
            <div className="vd-hint">
              Rule 8-102 1): when the connected load is unknown, voltage drop is based on
              80% of the overcurrent device rating.
            </div>
          </>
        )}

        {state.loadMode === "amps" && (
          <div className="vd-row">
            <input
              className="vd-input" type="number" inputMode="decimal" autoFocus
              value={state.amps} placeholder="0"
              onChange={e => set({ amps: e.target.value })}
            />
            <span className="vd-unit">A connected load{fRow?.f === 1.73 ? " (line current)" : ""}</span>
          </div>
        )}

        {state.loadMode === "watts" && (
          <>
            <div className="vd-row">
              <input
                className="vd-input" type="number" inputMode="numeric" autoFocus
                value={state.watts} placeholder="0"
                onChange={e => set({ watts: e.target.value })}
              />
              <span className="vd-unit">W</span>
              <span className="vd-derived">→ {current.toFixed(1)} A</span>
            </div>
            <div className="vd-hint">
              Rule 8-100: I = W / {voltage || "V"}{fRow?.f === 1.73 ? " / 1.73 (balanced 3-phase)" : ""}.
            </div>
          </>
        )}
      </div>

      {/* ── Conductor ── */}
      <div className="vd-section">
        <div className="vd-section-title">Conductor</div>
        <div className="vd-row" style={{ marginBottom: 8 }}>
          <div className="vd-pills">
            <button className={`vd-pill${state.material === "cu" ? " active" : ""}`} onClick={() => set({ material: "cu" })}>Copper</button>
            <button
              className={`vd-pill${state.material === "al" ? " active" : ""}`}
              onClick={() => set({ material: "al", ...(state.size === "14" ? { size: "12" } : {}) })}
            >Aluminum</button>
          </div>
          <select
            className="vd-select" style={{ width: "auto", flex: 1, minWidth: 110 }}
            value={state.size}
            onChange={e => set({ size: e.target.value })}
          >
            {SIZES.filter(s => state.material === "cu" || s !== "14").map(s => (
              <option key={s} value={s}>{labelForSize(s)}</option>
            ))}
          </select>
        </div>

        <div className="vd-row" style={{ marginBottom: 8 }}>
          <span className="vd-label">Run in</span>
          <div className="vd-pills">
            <button className={`vd-pill sm${state.install === "cable" ? " active" : ""}`} onClick={() => set({ install: "cable" })}>Cable</button>
            <button className={`vd-pill sm${state.install === "raceway" ? " active" : ""}`} onClick={() => set({ install: "raceway" })}>Raceway</button>
          </div>
          <span className="vd-hint" style={{ marginTop: 0 }}>
            Cable = NMD90 / TECK / AC90 assemblies · Raceway = singles (T90/RW90) in EMT, PVC, rigid
          </span>
        </div>

        <div className="vd-row" style={{ marginBottom: 8 }}>
          <span className="vd-label">Power factor</span>
          <div className="vd-pills">
            {isDc ? (
              <button className="vd-pill sm active" disabled>DC</button>
            ) : (
              ["100", "90", "80"].map(p => (
                <button key={p}
                  className={`vd-pill sm${state.pf === p ? " active" : ""}`}
                  onClick={() => set({ pf: p })}
                >{p}%</button>
              ))
            )}
          </div>
          {!isDc && (
            <span className="vd-hint" style={{ marginTop: 0 }}>
              100% resistive (heat, WH) · 90% mixed · 80% motors
            </span>
          )}
        </div>

        <div className="vd-row" style={{ marginBottom: 8 }}>
          <span className="vd-label">Operating temp</span>
          <div className="vd-pills">
            {[60, 75, 90].map(t => (
              <button key={t}
                className={`vd-pill sm${state.temp === t ? " active" : ""}`}
                onClick={() => set({ temp: t })}
              >{t} °C</button>
            ))}
          </div>
          <span className="vd-hint" style={{ marginTop: 0 }}>
            75 °C = Table D3 base · Note 3: ×0.95 / ×1.05
          </span>
        </div>

        <div className="vd-row">
          <span className="vd-label">Parallel runs / phase</span>
          <div className="vd-pills">
            {[1, 2, 3, 4].map(n => (
              <button key={n}
                className={`vd-pill sm${state.parallel === n ? " active" : ""}`}
                onClick={() => set({ parallel: n })}
              >{n}</button>
            ))}
          </div>
        </div>

        {showElv && (
          <label className="vd-check" style={{ marginTop: 10 }}>
            <input
              type="checkbox" checked={state.elv}
              onChange={e => set({ elv: e.target.checked })}
            />
            Extra-low-voltage power circuit — Table D3 Note 4 (×0.86, load ≤ ⅔ of Cu 60 °C ampacity)
          </label>
        )}
      </div>

      {/* ── Run length ── */}
      <div className="vd-section">
        <div className="vd-section-title">Run Length (one-way)</div>
        <div className="vd-row">
          <input
            className="vd-input" type="number" inputMode="decimal"
            value={state.length}
            onChange={e => set({ length: e.target.value })}
          />
          <div className="vd-pills">
            <button className={`vd-pill sm${state.lengthUnit === "m" ? " active" : ""}`}
              onClick={() => set({ lengthUnit: "m" })}>metres</button>
            <button className={`vd-pill sm${state.lengthUnit === "ft" ? " active" : ""}`}
              onClick={() => set({ lengthUnit: "ft" })}>feet</button>
          </div>
          {state.lengthUnit === "ft" && Number(state.length) > 0 && (
            <span className="vd-derived">= {lengthM.toFixed(1)} m</span>
          )}
        </div>
      </div>

      {/* ── Limit ── */}
      <div className="vd-section">
        <div className="vd-section-title">Voltage Drop Limit</div>
        <div className="vd-row">
          <div className="vd-pills">
            <button className={`vd-pill${state.limitMode === "3" ? " active" : ""}`}
              onClick={() => set({ limitMode: "3" })}>3% — branch / feeder</button>
            <button className={`vd-pill${state.limitMode === "5" ? " active" : ""}`}
              onClick={() => set({ limitMode: "5" })}>5% — total</button>
            <button className={`vd-pill${state.limitMode === "custom" ? " active" : ""}`}
              onClick={() => set({ limitMode: "custom" })}>Custom</button>
          </div>
          {state.limitMode === "custom" && (
            <>
              <input
                className="vd-input" style={{ width: 60 }} type="number" inputMode="decimal"
                value={state.limitCustom}
                onChange={e => set({ limitCustom: e.target.value })}
              />
              <span className="vd-unit">%</span>
            </>
          )}
        </div>
        <div className="vd-hint">
          Rule 8-102 1): max 3% in a feeder or branch circuit; max 5% from the supply
          side of the consumer's service to the point of utilization.
        </div>
      </div>

      {/* ── Result ── */}
      {!result.ok ? (
        <div className="vd-error">{result.error}</div>
      ) : (
        <div className={`vd-result ${result.pass ? "pass" : "fail"}`}>
          <div className="vd-result-head">
            <span className={`vd-result-pct ${result.pass ? "pass" : "fail"}`}>
              {result.vdPct.toFixed(2)}%
            </span>
            <span className={`vd-result-verdict ${result.pass ? "pass" : "fail"}`}>
              {result.pass ? `✓ WITHIN ${limitPct}% LIMIT` : `✗ EXCEEDS ${limitPct}% LIMIT`}
            </span>
          </div>

          <div className="vd-stats">
            <div className="vd-stat">
              <div className="vd-stat-label">Voltage drop</div>
              <div className="vd-stat-value">{result.vdVolts.toFixed(2)} V</div>
            </div>
            <div className="vd-stat">
              <div className="vd-stat-label">At the load</div>
              <div className="vd-stat-value">{result.voltsAtLoad.toFixed(1)} V</div>
            </div>
            <div className="vd-stat">
              <div className="vd-stat-label">Max length @ {limitPct}%</div>
              <div className="vd-stat-value">{fmtLen(result.maxLengthM)}</div>
            </div>
          </div>

          <div className="vd-trace">
            VD = K×f×I×L/1000 = {result.kBase}{result.multiplier !== 1 ? `×${result.multiplier}` : ""}
            {result.parallel > 1 ? `÷${result.parallel}` : ""} × {result.f} × {current.toFixed(1)} × {lengthM.toFixed(1)} / 1000
            = {result.vdVolts.toFixed(3)} V
          </div>

          <div className="vd-detail">
            K = <b>{result.kBase} Ω/km</b> (Table D3, {labelForSize(state.size)}{" "}
            {state.material === "cu" ? "copper" : "aluminum"}, {
              result.kColumn === "dc" ? "DC column"
                : result.kColumn === "pf100" ? "100% pf — cable or raceway"
                : `${state.install} @ ${state.pf}% pf`
            }) · <b>{result.multiplierNote}</b>
            {" · "}Ampacity ({state.material === "cu" ? "Table 2" : "Table 4"}, {result.ampacityTemp} °C):{" "}
            <b>{result.ampacity} A</b>{result.parallel > 1 ? ` per run × ${result.parallel}` : ""} vs{" "}
            {result.perRunCurrent.toFixed(1)} A{result.parallel > 1 ? " per run" : ""} —{" "}
            <b style={{ color: result.ampacityExceeded ? "#ef4444" : "#22c55e" }}>
              {result.ampacityExceeded ? "EXCEEDED" : "OK"}
            </b>
          </div>

          {minSizes && (minSizes.minSizeBoth || minSizes.minSizeVd) && (
            <div className="vd-reco">
              {minSizes.minSizeBoth ? (
                <>Minimum {state.material === "cu" ? "copper" : "aluminum"} size for this run:{" "}
                  <b>{labelForSize(minSizes.minSizeBoth)}</b> (meets {limitPct}% drop and ampacity
                  {state.parallel > 1 ? `, ${state.parallel} parallel runs — sizes below 1/0 excluded per Rule 12-108` : ""})
                  {minSizes.minSizeVd && minSizes.minSizeVd !== minSizes.minSizeBoth && (
                    <> — {labelForSize(minSizes.minSizeVd)} would satisfy voltage drop alone but not ampacity</>
                  )}.</>
              ) : (
                <>No single listed conductor meets both limits — <b>{labelForSize(minSizes.minSizeVd)}</b>{" "}
                  meets {limitPct}% drop; check parallel runs for ampacity.</>
              )}
            </div>
          )}
          {minSizes && !minSizes.minSizeVd && (
            <div className="vd-warn">
              No conductor size in Table D3 meets {limitPct}% at this current and length —
              consider parallel runs, a higher supply voltage, or a shorter route.
            </div>
          )}

          {result.warnings.map((w, i) => (
            <div key={i} className="vd-warn">⚠ {w}</div>
          ))}

          {t68 !== null && (
            <div className="vd-t68">
              <b>Table 68 alternate path (Rule 8-102 3)):</b> for dwelling-unit general-use
              branch circuits, {labelForSize(state.size)} copper (90 °C) on a {state.breaker} A
              breaker is deemed acceptable up to <b>{t68} m</b> measured from the supply side
              of the consumer's service — even where the 3% calculation above fails. Not
              applicable to appliance, kitchen, heating/cooking, or dedicated receptacle circuits.
            </div>
          )}

          <div className="vd-footnote">
            CEC 2024 (CSA C22.1:24) Rule 8-102 &amp; Appendix D Table D3 — K values at 75 °C
            conductor operating temperature, 2/3/4 insulated conductors per cable or raceway.
            Ampacity check is Tables 2/4 (≤3 conductors, 30 °C ambient) — apply Table 5A/5C
            corrections separately where ambient or conductor count differs.
          </div>
        </div>
      )}
    </div>
  );
}
